import { useState, useMemo, useEffect, createContext, useContext } from "react";

// Los seis idiomas se cargan bajo demanda (1-10-2026): importados de forma estática sumaban ~600 KB
// de JSON al main.js (1,22 MB; 375 KB comprimido) aunque cada visita solo usa uno. Con el HTML
// prerenderizado ya visible, React espera a tener el diccionario antes de montar: ver index.js.
// de/fr solo cubren las páginas de producto: ver utils/langScope.js.
const LOADERS = {
  en: () => import("./locales/en.json"),
  es: () => import("./locales/es.json"),
  it: () => import("./locales/it.json"),
  pt: () => import("./locales/pt.json"),
  de: () => import("./locales/de.json"),
  fr: () => import("./locales/fr.json"),
};
export const SUPPORTED_LANGS = Object.keys(LOADERS);
export const DEFAULT_LANG = "en";

const cache = {};
const pending = {};

/** Carga (una sola vez) el diccionario de un idioma; un idioma desconocido carga el inglés. */
export function loadLocale(lang) {
  const l = LOADERS[lang] ? lang : DEFAULT_LANG;
  if (cache[l]) return Promise.resolve(cache[l]);
  if (!pending[l]) {
    pending[l] = LOADERS[l]().then((m) => {
      cache[l] = m.default || m;
      return cache[l];
    });
  }
  return pending[l];
}

const TranslationContext = createContext({
  lang: "en",
  t: (key) => key,
});

export function useTranslation() {
  return useContext(TranslationContext);
}

export function TranslationProvider({ lang = "en", children }) {
  const key = LOADERS[lang] ? lang : DEFAULT_LANG;
  const [dictionary, setDictionary] = useState(() => cache[key] || null);

  // Al cambiar de idioma se sigue enseñando el anterior hasta que llega el nuevo (sin parpadeo en blanco).
  useEffect(() => {
    let alive = true;
    loadLocale(key).then((d) => {
      if (alive) setDictionary(d);
    });
    return () => {
      alive = false;
    };
  }, [key]);

  const t = useMemo(() => {
    // El segundo argumento sirve para dos cosas: interpolar `{{var}}` y, con
    // `defaultValue`, dar un texto de reserva cuando la clave no existe.
    //
    // Ese `defaultValue` se añadió porque varias llamadas de `App.js` ya lo
    // pasaban dando por hecho la semántica de react-i18next, y esta `t()` lo
    // ignoraba en silencio: `footer.sectors` se renderizó en producción como la
    // cadena literal «footer.sectors», en el pie de las cuatro versiones de
    // idioma, pese a tener el defaultValue puesto en el código.
    return (key, vars = {}) => {
      const parts = key.split(".");
      let result = dictionary;
      for (const p of parts) {
        if (result && p in result) result = result[p];
        else return vars.defaultValue !== undefined ? vars.defaultValue : key;
      }
      if (typeof result === "string" && vars && Object.keys(vars).length > 0) {
        let text = result;
        for (const [k, v] of Object.entries(vars)) {
          text = text.replace(new RegExp(`{{${k}}}`, "g"), v);
        }
        return text;
      }
      return result;
    };
  }, [dictionary]);

  // Hasta tener el diccionario no se pinta nada: con claves sueltas se vería «footer.tools».
  if (!dictionary) return null;

  return (
    <TranslationContext.Provider value={{ lang, t }}>
      {children}
    </TranslationContext.Provider>
  );
}
